import React from 'react';
import { AlertCircle, AlertTriangle, Info, ArrowRight } from 'lucide-react';
import { RevenueAnomaly } from '../../types';

interface DashboardAlertsProps {
  anomalies: RevenueAnomaly[];
  currencySymbol: string;
}

const DashboardAlerts: React.FC<DashboardAlertsProps> = ({ anomalies, currencySymbol }) => {
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return {
          icon: AlertCircle,
          badge: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400 border border-red-200 dark:border-red-800',
        };
      case 'Warning':
        return {
          icon: AlertTriangle,
          badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400 border border-amber-200 dark:border-amber-800',
        };
      default:
        return {
          icon: Info,
          badge: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800',
        };
    }
  };

  const handleNavigate = (title: string) => {
    const targetView = title.toLowerCase().includes('failed') || title.toLowerCase().includes('churn')
      ? 'dunning'
      : title.toLowerCase().includes('cart')
      ? 'abandoned'
      : 'plans';

    if ((window as any).setActiveView) {
      (window as any).setActiveView(targetView);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900 dark:text-white">Revenue Signals & Opportunities</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">Automated detections requiring attention or intervention.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {anomalies.length === 0 ? (
          <div className="md:col-span-3 bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xs flex items-center gap-4">
            <div className="p-3 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 rounded-xl shrink-0">
              <Info className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900 dark:text-white">Zero Active Revenue Anomalies</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                All subscriptions, payment gateways, and checkout flows are operating in good standing with zero detected churn risks.
              </p>
            </div>
          </div>
        ) : (
          anomalies.map((anom) => {
          const { icon: SeverityIcon, badge } = getSeverityBadge(anom.severity);
          const isNegative = anom.impactValue < 0;

          return (
            <div
              key={anom.id}
              className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-all group"
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${badge}`}>
                    <SeverityIcon className="w-3 h-3" />
                    {anom.severity}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500">Action Required</span>
                </div>

                <h4 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  {anom.title}
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                  {anom.description}
                </p>
              </div>

              <div className="mt-5 pt-3.5 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <span className={`text-xs font-extrabold ${isNegative ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {isNegative ? '-' : '+'}{currencySymbol}{Math.abs(anom.impactValue).toLocaleString()} Impact
                </span>
                <button
                  onClick={() => handleNavigate(anom.title)}
                  className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                >
                  <span>Resolve</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        }))}
      </div>
    </div>
  );
};

export default DashboardAlerts;
